import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useStore } from '../store';

// ── Mouse-wheel hint (Calem, report d7af4dee) ─────────────────────────────────────────────────────────────
// The up-and-down scroll bars are gone from every box (global.css), and in their place: "when the user hovers over an
// area that is scrollable, a pop-up appears saying, "Use your mouse wheel to scroll up and down." The first 10 times
// they visit that page, after that you no longer have to deliver the pop-up."
// Mounted once in AuthGate (App.tsx), so it rides along on every staff page. One mouseover listener on the document
// looks up from whatever the mouse is on to the nearest box that scrolls up and down and holds more than it shows — a
// box that used to show a bar — and the line follows the mouse until the mouse leaves that box. The first turn of the
// wheel takes it away for the rest of the visit: whoever turns the wheel has found it.
// A visit is a page, or a Dashboard tab (?tab=), being entered, counted per user in this browser (localStorage
// mf_scrollhint:<userId>:<page>), so nothing is kept on the server: from its 11th visit on, a page never shows the line
// again. Only where there is a mouse to point with — a phone has no wheel.
const VISITS = 10;
const visitKey = (userId: string, page: string) => `mf_scrollhint:${userId}:${page}`;

// The nearest box at or above `from` that scrolls up and down and has more in it than it shows.
function scrollBoxOf(from: EventTarget | null): Element | null {
  for (let el = from instanceof Element ? from : null; el; el = el.parentElement) {
    if (el.scrollHeight > el.clientHeight && /^(auto|scroll)$/.test(getComputedStyle(el).overflowY)) return el;
  }
  return null;
}

export default function ScrollHint() {
  const { t } = useTranslation();
  const userId = useStore(s => s.me?.userId);
  const loc = useLocation();
  const tab = new URLSearchParams(loc.search).get('tab');
  const page = tab ? `${loc.pathname}?tab=${tab}` : loc.pathname;
  const [live, setLive] = useState('');   // the page whose visit still shows the line ('' = none)
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);   // the mouse, while the line shows
  const counted = useRef('');   // StrictMode runs the effect twice for one entry, and it is one visit

  useEffect(() => {
    if (!userId || !window.matchMedia('(hover: hover)').matches) return;
    const key = visitKey(userId, page);
    if (counted.current === key) return;
    counted.current = key;
    try {
      const seen = Number(localStorage.getItem(key)) || 0;
      if (seen >= VISITS) { setLive(''); return; }
      localStorage.setItem(key, String(seen + 1));
      setLive(page);
    } catch { setLive(''); }   // storage refused: a visit that cannot be counted shows nothing
  }, [userId, page]);

  const on = live === page;
  useEffect(() => {
    if (!on) return;
    let box: Element | null = null;   // the box the line is showing for
    const hide = () => { box?.removeEventListener('mouseleave', hide); box = null; setAt(null); };
    const over = (e: MouseEvent) => {
      const b = scrollBoxOf(e.target);
      if (b === box) return;
      hide();
      if (!b) return;
      box = b;
      b.addEventListener('mouseleave', hide);
      setAt({ x: e.clientX, y: e.clientY });
    };
    const move = (e: MouseEvent) => { if (box) setAt({ x: e.clientX, y: e.clientY }); };
    const wheel = () => { hide(); setLive(''); };
    document.addEventListener('mouseover', over, true);
    document.addEventListener('mousemove', move, true);
    document.addEventListener('wheel', wheel, { capture: true, passive: true });
    return () => {
      document.removeEventListener('mouseover', over, true);
      document.removeEventListener('mousemove', move, true);
      document.removeEventListener('wheel', wheel, true);
      hide();
    };
  }, [on]);

  if (!on || !at) return null;
  // Below and to the right of the pointer, where a tooltip sits; turned to the other side near the window's right or
  // bottom edge. Over every dialog, and every click goes through it.
  const flipX = at.x > window.innerWidth - 340;
  const flipY = at.y > window.innerHeight - 64;
  return (
    <div role="tooltip" className="fade-in" style={{
      position: 'fixed', left: at.x, top: at.y, zIndex: 20000, pointerEvents: 'none',
      transform: `translate(${flipX ? 'calc(-100% - 10px)' : '14px'}, ${flipY ? 'calc(-100% - 10px)' : '20px'})`,
      display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
      background: 'var(--text-primary)', color: 'var(--bg-surface)', fontSize: 12, fontWeight: 500,
      padding: '6px 10px', borderRadius: 6, boxShadow: 'var(--shadow-md)',
    }}>
      <i className="ti ti-selector" style={{ fontSize: 15 }} aria-hidden />
      {t('scroll_hint_wheel')}
    </div>
  );
}
